// Story 11.4 — Mutation para `POST /bots/:id/deploy`.
// Story 11.5 — wirea al validation-store para que el indicador refleje el
// estado del último deploy y proyecta errores semánticos del backend.

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ApiError, type DeployInput, deploymentsApi } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-keys';
import { useValidationStore } from '@/stores/validation-store';

export function useDeployBot(botId: number) {
  const qc = useQueryClient();
  const startValidation = useValidationStore((s) => s.start);
  const setValidationResult = useValidationStore((s) => s.setResult);
  return useMutation({
    mutationFn: (input: DeployInput) => deploymentsApi.deploy(botId, input),
    onMutate: () => {
      startValidation();
    },
    onSuccess: ({ deployment }) => {
      setValidationResult([], []);
      toast.success(`Deploy ${deployment.version} aplicado.`);
      // Invalida queries dependientes.
      void qc.invalidateQueries({ queryKey: queryKeys.bot(botId) });
      void qc.invalidateQueries({ queryKey: queryKeys.deployments(botId) });
      void qc.invalidateQueries({ queryKey: queryKeys.bots() });
      // P-444 (review 11.4): el deploy cambia el config activo → métricas
      // y conversaciones derivadas pueden mostrar versiones stale. Match
      // por prefix `['bots', botId, 'analytics']` y `'conversations'`.
      void qc.invalidateQueries({ queryKey: ['bots', botId, 'analytics'] });
      void qc.invalidateQueries({ queryKey: ['bots', botId, 'conversations'] });
    },
    onError: (err) => {
      // Story 11.5 — proyecta errores del backend (semánticos) al validation
      // store. Si la response venía con `error.errors[]` (lista del
      // validator), los expone individualmente; sino, un error sintético.
      const body = (err as ApiError).body as
        | { errors?: Array<{ code?: string; message?: string; location?: unknown }> }
        | undefined;
      const projected =
        err instanceof ApiError && Array.isArray(body?.errors)
          ? body.errors.map((e) => ({
              code: e.code ?? 'DEPLOY_ERROR',
              message: e.message ?? String(e),
              location: (e as { location?: { line?: number; column?: number; filePath?: string } })
                .location,
            }))
          : [{ code: 'DEPLOY_ERROR', message: (err as Error).message }];
      setValidationResult(projected, []);
      toast.error(`No se pudo deployar: ${(err as Error).message}`);
    },
  });
}
